/* eslint-disable react/destructuring-assignment */
import { IPopoverProps, MenuDivider, MenuItem } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { ItemRendererProps } from '@blueprintjs/select';
import classNames from 'classnames';
import * as React from 'react';

import { noop } from 'bones/utils/noop';
import { SELECT_ITEM_DIVIDER_VALUE } from 'bones/utils/select/createSelectItemDivider';
import { BaseSelectItem, SelectItem } from 'bones/utils/select/types';

export interface SelectItemRendererOptions<
  SubItemType extends SelectItem | null,
  ItemType extends SelectItem<SubItemType>,
  SelectionType extends
    | SubItemType
    | ItemType
    | SubItemType[]
    | ItemType[]
    | null
> {
  // required
  selected: SelectionType;

  // optional
  itemClassName?: string;
  itemMultiline?: boolean;
  itemPopoverProps?: IPopoverProps;
  itemTestId?: (value: string, idx?: number) => string | void;
  subItemTestId?: (
    value: string,
    subValue: string,
    idx?: number,
    subIdx?: number
  ) => string | void;
}

export const selectItemRenderer =
  <
    SubItemType extends SelectItem | null,
    ItemType extends SelectItem<SubItemType>,
    SelectionType extends
      | SubItemType
      | ItemType
      | SubItemType[]
      | ItemType[]
      | null
  >(
    rendererOptions: SelectItemRendererOptions<
      SubItemType,
      ItemType,
      SelectionType
    > & {
      handleSelect: (
        item: ItemType | NonNullable<SubItemType>,
        evt?: React.SyntheticEvent<HTMLElement>
      ) => void;
    }
  ) =>
  // eslint-disable-next-line react/display-name
  (item: ItemType, rendererProps: ItemRendererProps) => {
    const {
      itemClassName,
      itemMultiline,
      itemPopoverProps,
      itemTestId = noop,
      subItemTestId = noop,
      selected,
      handleSelect,
    } = rendererOptions;
    const { handleClick, modifiers, index } = rendererProps;

    // istanbul ignore if
    if (!modifiers.matchesPredicate) {
      return null;
    }

    if (item.value === SELECT_ITEM_DIVIDER_VALUE) {
      return (
        <MenuDivider key={`${item.value}-${item.text}`} title={item.text} />
      );
    }

    const isItemSelected = <T extends BaseSelectItem | null>({
      items,
      value,
    }: {
      items?: null extends T ? never : T[];
      value: string;
    }): boolean => {
      if (items?.length) {
        return items.some(
          (childItem) => childItem && isItemSelected<T>(childItem)
        );
      }
      return Array.isArray(selected)
        ? selected.some((selectedItem) => selectedItem?.value === value)
        : selected?.value === value;
    };
    const isSelected = isItemSelected(item);

    const getHasSubItems = <T extends SelectItem | null>(
      parentItem: SelectItem<T>
    ): boolean =>
      Boolean(
        parentItem.items?.some(
          (childItem) =>
            childItem && (!childItem.items || getHasSubItems(childItem))
        )
      );
    const hasSubItems = getHasSubItems(item);

    if (!item.items || !hasSubItems) {
      return (
        <MenuItem
          key={item.value}
          active={modifiers.active}
          className={classNames(itemClassName, item.className)}
          data-testid={itemTestId(item.value, index)}
          icon={item.icon || (isSelected ? IconNames.TICK : IconNames.BLANK)}
          multiline={itemMultiline}
          onClick={handleClick}
          text={item.text}
        />
      );
    }

    const itemFilter = (item: SubItemType): item is NonNullable<SubItemType> =>
      Boolean(item);

    const renderSubItems = (
      parentItem: ItemType | NonNullable<SubItemType>,
      subItems: SubItemType[],
      useActiveModifier?: boolean
    ) => {
      const filteredItems = subItems.filter(itemFilter);
      if (!filteredItems.length) {
        return null;
      }

      if (parentItem.collapseSingleItem && filteredItems.length === 1) {
        const [singleItem] = filteredItems;
        return (
          <MenuItem
            key={singleItem.value}
            active={useActiveModifier ? modifiers.active : undefined}
            className={classNames(itemClassName, parentItem.className)}
            data-testid={subItemTestId(
              parentItem.value,
              singleItem.value,
              index,
              0
            )}
            icon={
              parentItem.icon || (isSelected ? IconNames.TICK : IconNames.BLANK)
            }
            multiline={itemMultiline}
            onClick={(e: React.SyntheticEvent<HTMLElement, Event>) =>
              handleSelect(singleItem, e)
            }
            text={parentItem.text}
          />
        );
      }

      return (
        // eslint-disable-next-line @blueprintjs/no-deprecated-components
        <MenuItem
          key={parentItem.value}
          active={useActiveModifier ? modifiers.active : undefined}
          className={classNames(itemClassName, parentItem.className)}
          data-testid={itemTestId(parentItem.value, index)}
          icon={
            parentItem.icon || (isSelected ? IconNames.TICK : IconNames.BLANK)
          }
          multiline={itemMultiline}
          popoverProps={itemPopoverProps}
          text={parentItem.text}
        >
          {filteredItems.map((subItem, subItemIdx) =>
            subItem.items ? (
              renderSubItems(subItem, subItem.items)
            ) : (
              <MenuItem
                key={subItem.value}
                className={classNames(itemClassName, subItem.className)}
                data-testid={subItemTestId(
                  parentItem.value,
                  subItem.value,
                  index,
                  subItemIdx
                )}
                icon={
                  isItemSelected(subItem) ? IconNames.TICK : IconNames.BLANK
                }
                multiline={itemMultiline}
                onClick={(e: React.SyntheticEvent<HTMLElement, Event>) =>
                  handleSelect(subItem, e)
                }
                text={subItem.text}
              />
            )
          )}
        </MenuItem>
      );
    };

    return renderSubItems(item, item.items.filter(itemFilter), true);
  };
